import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Tag, X } from "lucide-react";
import { fbPurchase } from "@/lib/pixel";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [addr, setAddr] = useState({ name: user?.name || "", phone: "", line1: "", city: "", state: "", pincode: "" });
  const [processing, setProcessing] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // { code, percent, discount, label }
  const [applying, setApplying] = useState(false);

  if (!user) {
    return (
      <div className="max-w-lg mx-auto py-24 px-6 text-center" data-testid="checkout-auth-required">
        <h1 className="font-display text-4xl">LOGIN TO CHECKOUT</h1>
        <button onClick={() => navigate("/login?next=/checkout")} className="mt-6 bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-6 py-3 font-mono-b font-bold uppercase">Login</button>
      </div>
    );
  }

  if (items.length === 0) {
    return <div className="p-16 text-center font-mono-b" data-testid="checkout-empty">Bag is empty. <a href="/shop" className="underline">Shop now</a>.</div>;
  }

  const canPay = ["name","phone","line1","city","state","pincode"].every(k => addr[k].trim());

  const handlePay = async () => {
    if (processing) return;  // double-submit protection
    if (!canPay) { toast.error("Fill in the address"); return; }
    setProcessing(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Razorpay failed to load");
      const { data } = await api.post("/orders/create", {
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity, size: i.size, color: i.color })),
        address: addr,
        coupon_code: coupon?.code || null,
      });
      const { order, razorpay_key_id } = data;
      const options = {
        key: razorpay_key_id,
        amount: Math.round(order.total * 100),
        currency: "INR",
        name: "OUTLAW-D",
        description: "Streetwear drop",
        order_id: order.razorpay_order_id,
        prefill: { name: addr.name, email: user.email, contact: addr.phone },
        theme: { color: "#000000" },
        handler: async (resp) => {
          try {
            await api.post("/orders/verify", {
              order_id: order.order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            fbPurchase(order);
            clear();
            toast.success("Payment successful");
            navigate("/account");
          } catch (e) {
            toast.error("Payment verification failed");
          }
        },
        modal: { ondismiss: () => setProcessing(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setProcessing(false);
    }
  };

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return toast.error("Enter a coupon code");
    setApplying(true);
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal: total });
      setCoupon(data);
      toast.success(`${data.code} applied — ${data.percent}% off`);
    } catch (err) {
      setCoupon(null);
      toast.error(err.response?.data?.detail || "Invalid coupon");
    } finally {
      setApplying(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput("");
  };

  const ship = 99;
  const discount = coupon ? +(total * coupon.percent / 100).toFixed(2) : 0;
  const platformFee = +(total * 0.07).toFixed(2);
  const grandTotal = +(total - discount + platformFee + ship).toFixed(2);
  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10" data-testid="checkout-page">
      <h1 className="font-display text-5xl md:text-6xl mb-6">CHECKOUT</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="bg-white border-2 border-black p-6 brutal-shadow">
          <h2 className="font-display text-2xl mb-4">SHIPPING ADDRESS</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              ["name","FULL NAME"], ["phone","PHONE"],
              ["line1","ADDRESS LINE"], ["city","CITY"],
              ["state","STATE"], ["pincode","PINCODE"],
            ].map(([k, label]) => (
              <div key={k} className={k === "line1" ? "sm:col-span-2" : ""}>
                <label className="text-xs font-mono-b font-bold uppercase">{label}</label>
                <input
                  data-testid={`checkout-${k}`}
                  value={addr[k]}
                  onChange={(e) => setAddr({ ...addr, [k]: e.target.value })}
                  className="brutal-input w-full mt-1"
                />
              </div>
            ))}
          </div>
        </div>
        <aside className="bg-black text-white border-2 border-black p-6 brutal-shadow h-fit" data-testid="checkout-summary">
          <h2 className="font-display text-2xl mb-3 text-[#D4FF00]">SUMMARY</h2>
          {items.map(i => (
            <div key={`${i.product_id}-${i.size}-${i.color}`} className="flex justify-between text-sm font-mono-b py-1 border-b border-white/10">
              <span>{i.name}{i.size ? ` · ${i.size}` : ""}</span><span>₹{i.price}</span>
            </div>
          ))}
          <div className="flex justify-between font-mono-b py-2 border-b border-white/20 mt-2"><span>Platform fee (7%)</span><span>₹{platformFee}</span></div>
          <div className="flex justify-between font-mono-b py-2 border-b border-white/20"><span>Shipping</span><span>₹{ship}</span></div>

          {/* COUPON */}
          <div className="mt-3 border-2 border-dashed border-[#D4FF00] p-3" data-testid="coupon-block">
            <p className="text-[10px] font-mono-b font-bold uppercase text-[#D4FF00] tracking-wider flex items-center gap-1"><Tag className="w-3 h-3" /> COUPON CODE</p>
            {!coupon ? (
              <div className="mt-2 flex gap-2">
                <input
                  data-testid="coupon-input"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCoupon())}
                  placeholder="Enter code"
                  className="flex-1 bg-white text-black border-2 border-white px-2 py-1 font-mono-b text-sm uppercase"
                />
                <button
                  type="button"
                  disabled={applying}
                  onClick={applyCoupon}
                  data-testid="coupon-apply"
                  className="bg-[#D4FF00] text-black border-2 border-white px-3 py-1 font-mono-b text-xs font-bold uppercase disabled:opacity-50"
                >
                  {applying ? "..." : "APPLY"}
                </button>
              </div>
            ) : (
              <div className="mt-2 flex items-center justify-between gap-2" data-testid="coupon-applied">
                <div>
                  <p className="font-display text-lg leading-none text-[#D4FF00]">{coupon.code}</p>
                  <p className="text-[10px] font-mono-b text-white/70">{coupon.label}</p>
                </div>
                <button
                  type="button"
                  onClick={removeCoupon}
                  data-testid="coupon-remove"
                  className="border-2 border-white bg-white text-black p-1"
                  aria-label="remove coupon"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            <p className="text-[9px] font-mono-b text-white/40 mt-2">Try <b>Newbuyer</b> (first order) or <b>Onceagain22</b> (returning).</p>
          </div>

          {coupon && (
            <div className="flex justify-between font-mono-b py-2 border-b border-white/20 mt-2 text-[#D4FF00]" data-testid="coupon-discount-line">
              <span>Discount ({coupon.percent}%)</span><span>-₹{discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between font-mono-b font-bold py-3 text-2xl"><span>TOTAL</span><span className="text-[#D4FF00]">₹{grandTotal}</span></div>
          <button disabled={processing} onClick={handlePay} data-testid="checkout-pay" className="w-full bg-[#D4FF00] text-black border-2 border-white brutal-shadow py-3 mt-3 font-mono-b font-bold uppercase disabled:opacity-50">
            {processing ? "PROCESSING..." : "PAY NOW"}
          </button>
          <p className="text-xs font-mono-b text-white/60 mt-3 text-center">Powered by Razorpay · UPI, Cards, Netbanking</p>
        </aside>
      </div>
    </div>
  );
}
